import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { useContact } from "@/context/ContactContext";
import wordmark from "@/assets/wordmark.png";

const OD_LOGO =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_wallet-connect-demo-1/artifacts/00z5nh51_Open%20Deposit%20Logo.png";

const APP_URL = "https://app.opendeposit.xyz";

const LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Why", href: "#why" },
  { label: "Pricing", href: "#pricing" },
  { label: "Docs", href: "https://docs.opendeposit.xyz", external: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { setOpen } = useContact();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled; // over white hero when not scrolled
  const textColor = "text-[#0a0a0a]";

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div
        className={`transition-colors duration-500 ${
          dark
            ? "bg-white/80 backdrop-blur-xl border-b border-black/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-[68px] flex items-center justify-between">
          <a
            href="#top"
            data-testid="navbar-logo"
            className="flex items-center gap-2.5 group"
          >
            <img
              src={OD_LOGO}
              alt="OD"
              className="h-9 w-9 rounded-lg"
            />
            <img
              src={wordmark}
              alt="OpenDeposit"
              className="h-5 md:h-[22px] w-auto"
            />
          </a>

          <div className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                data-testid={`nav-link-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={`text-sm font-medium transition-opacity hover:opacity-60 ${textColor}`}
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              data-testid="nav-contact-btn"
              onClick={() => setOpen(true)}
              className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors text-[#0a0a0a] hover:bg-black/5`}
            >
              Contact
            </button>
            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-launch-btn"
              className="text-sm font-bold px-5 py-2.5 rounded-full bg-[#0a0a0a] text-white hover:bg-black/85 transition-colors"
            >
              Launch App
            </a>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            className={`md:hidden p-2 rounded-lg ${textColor}`}
            aria-label="Menu"
          >
            {menuOpen ? <X size={26} weight="bold" /> : <List size={26} weight="bold" />}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white backdrop-blur-xl border-b border-black/10 overflow-hidden"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  target={l.external ? "_blank" : undefined}
                  rel={l.external ? "noopener noreferrer" : undefined}
                  onClick={() => setMenuOpen(false)}
                  className="text-[#0a0a0a] text-lg font-medium"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setOpen(true);
                  }}
                  className="flex-1 text-sm font-semibold px-4 py-3 rounded-full border border-black/15 text-[#0a0a0a]"
                >
                  Contact
                </button>
                <a href={APP_URL} target="_blank" rel="noopener noreferrer" className="flex-1 text-center text-sm font-bold px-4 py-3 rounded-full bg-[#0a0a0a] text-white">
                  Launch App
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
